import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useNavigate } from "@tanstack/react-router"
import { SignUpOutput } from "aws-amplify/auth"
import { Controller, FormProvider, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TermsAndConditions from "@/globalComponents/molecules/TermsAndConditions"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import {
  passwordValidationRule,
  requiredRule,
  validEmailRule,
} from "@/utilities/form"
import { toE164 } from "@/utilities/phone"

export const Route = createFileRoute("/auth/create-account")({
  component: RouteComponent,
})

interface CreateAccountFormSchema {
  firstName: string
  lastName: string
  phone: string
  email: string
  password: string
  confirmPassword: string
}

// Helper function to log mutation errors to Sentry
function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function RouteComponent() {
  const search = Route.useSearch()
  const { t } = useTranslation(["gated", "errors", "common"])
  const navigate = useNavigate()

  // Format phone number for US when coming from guest data

  const form = useForm<CreateAccountFormSchema>({
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: search?.phone ? toE164(search.phone) : "",
      email: search?.email || "",
      password: "",
      confirmPassword: "",
    },
    mode: "onBlur",
  })

  const passwordFieldState = form.getFieldState("password", form.formState)
  const confirmPasswordFieldState = form.getFieldState(
    "confirmPassword",
    form.formState
  )

  const { mutate, isPending } = useMutation<
    SignUpOutput,
    Error,
    CreateAccountFormSchema
  >({
    mutationFn,
    onSuccess,
    onError,
  })

  return (
    <>
      <Heading level={1} as={1}>
        {t("auth.createAccount.heading", { ns: "common" })}
      </Heading>
      <Text level={2} style={{ marginTop: "var(--space-2)" }}>
        {t("auth.createAccount.description", { ns: "common" })}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset disabled={isPending}>
            <div className="form-row">
              <Controller<CreateAccountFormSchema>
                name="firstName"
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                }}
                render={({
                  field: { ref, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField {...rest} isInvalid={invalid}>
                    <Label>
                      {t("auth.createAccount.firstName", { ns: "common" })}
                    </Label>
                    <TextField.Input innerRef={ref} placeholder="Alex" />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
              <Controller<CreateAccountFormSchema>
                name="lastName"
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                }}
                render={({
                  field: { ref, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField {...rest} isInvalid={invalid}>
                    <Label>
                      {t("auth.createAccount.lastName", { ns: "common" })}
                    </Label>
                    <TextField.Input innerRef={ref} placeholder="Smith" />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
            </div>
            <Controller<CreateAccountFormSchema>
              name="phone"
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
              }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>
                    {t("auth.createAccount.phoneNumber", { ns: "common" })}
                  </Label>
                  <TextField.PhoneWithCountrySelect
                    innerRef={ref}
                    defaultCountry="US"
                    placeholder="(123) 456-6789"
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Controller<CreateAccountFormSchema>
              name="email"
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
                pattern: validEmailRule(
                  t("common.validEmail", { ns: "errors" })
                ),
              }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>
                    {t("auth.createAccount.email", { ns: "common" })}
                  </Label>
                  <TextField.Input
                    innerRef={ref}
                    type="email"
                    placeholder="alex.smith@example.com"
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Controller<CreateAccountFormSchema>
              name="password"
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
                ...passwordValidationRule(t),
              }}
              render={({
                field: { ref, onChange, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  onChange={(v) => {
                    onChange(v)
                    if (passwordFieldState.isTouched) {
                      form.trigger("password")
                    }
                    if (confirmPasswordFieldState.isDirty) {
                      form.trigger("confirmPassword")
                    }
                  }}
                  {...rest}
                  isInvalid={invalid}
                >
                  <Label>
                    {t("auth.createAccount.password", { ns: "common" })}
                  </Label>
                  <TextField.Password innerRef={ref} />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Controller<CreateAccountFormSchema>
              name="confirmPassword"
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
                validate: (value) => {
                  const password = form.getValues("password")
                  return (
                    value === password ||
                    t("common.passwordMismatch", { ns: "errors" })
                  )
                },
              }}
              render={({
                field: { ref, onChange, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField
                  {...rest}
                  onChange={(v) => {
                    onChange(v)
                    if (
                      confirmPasswordFieldState.isTouched ||
                      passwordFieldState.isDirty
                    ) {
                      form.trigger("confirmPassword")
                    }
                  }}
                  isInvalid={invalid}
                >
                  <Label>
                    {t("auth.createAccount.confirmPassword", { ns: "common" })}
                  </Label>
                  <TextField.Password innerRef={ref} />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Text level={3} className="center">
              <TermsAndConditions qrLookupID={search.qrLookupID} />
            </Text>
            {form.formState.errors.root && (
              <FieldError className="center">
                {form.formState.errors.root.message}
              </FieldError>
            )}
            <Button buttonType="primary" type="submit" isPending={isPending}>
              {t("auth.createAccount.submitButton", { ns: "common" })}
            </Button>
          </fieldset>
        </form>
      </FormProvider>
      <Text level={2} className="center">
        {t("auth.createAccount.alreadyHaveAccount", { ns: "common" })}{" "}
        <LinkButton
          buttonType="primary"
          presentation="link"
          style={{ textDecorationLine: "underline" }}
          to="/auth/sign-in"
          search={search}
        >
          {t("auth.createAccount.signInLink", { ns: "common" })}
        </LinkButton>
        .
      </Text>
    </>
  )

  function onSubmit(data: CreateAccountFormSchema) {
    mutate(data)
  }

  async function mutationFn(data: CreateAccountFormSchema) {
    return api.auth.signUp({
      username: data.email,
      password: data.password,
      options: {
        userAttributes: {
          email: data.email,
          phone_number: toE164(data.phone),
          given_name: data.firstName,
          family_name: data.lastName,
        },
        autoSignIn: true,
      },
    })
  }

  function onSuccess(result: SignUpOutput, variables: CreateAccountFormSchema) {
    console.log("onSuccess", result, variables)

    // Email verification is always required for new accounts
    navigate({
      to: "/auth/verify",
      search: {
        ...search,
        email: variables.email,
        context: "create-account",
      },
    })
  }

  function onError(err: Error, variables: CreateAccountFormSchema) {
    logMutationError(err, {
      flow: "create-account",
      email: variables.email,
      qrLookupID: search.qrLookupID,
      additionalData: {
        firstName: variables.firstName,
        lastName: variables.lastName,
      },
    })
    handleAuthError(err, form, t)
  }
}
